import { Button, Modal, Text } from '@shared/components';
import { getDocumentExtension } from '@shared/components/doc-viewer/DocViewer';
import { MessageAttachment } from '@shared/types/conversation';
import { downloadFile } from '@shared/utils';
import { ReactNode } from 'react';
import {
  PiDownloadSimple,
  PiFile,
  PiFileCsv,
  PiFilePdf,
  PiFileText,
  PiFileXls,
} from 'react-icons/pi';

const FILE_TYPE_ICONS: Record<string, typeof PiFile> = {
  csv: PiFileCsv,
  pdf: PiFilePdf,
  txt: PiFileText,
  xls: PiFileXls,
  xlsm: PiFileXls,
  xlsx: PiFileXls,
};

type ChatAttachmentsProps = {
  alignment?: 'start' | 'end';
  attachments: MessageAttachment[];
  enableImagePreview?: boolean;
  renderDocumentPreview?: (attachment: MessageAttachment) => ReactNode;
};

export function ChatAttachments({
  alignment = 'start',
  attachments,
  enableImagePreview = true,
  renderDocumentPreview,
}: ChatAttachmentsProps) {
  return (
    <div
      className={`flex flex-col gap-1 ${
        alignment === 'end' ? 'items-end' : 'items-start'
      }`}
    >
      {attachments.map((attachment) => (
        <ChatAttachment
          key={attachment.id}
          attachment={attachment}
          enableImagePreview={enableImagePreview}
          renderDocumentPreview={renderDocumentPreview}
        />
      ))}
    </div>
  );
}

function ChatAttachment({
  attachment,
  enableImagePreview,
  renderDocumentPreview,
}: {
  attachment: MessageAttachment;
  enableImagePreview: boolean;
  renderDocumentPreview?: ChatAttachmentsProps['renderDocumentPreview'];
}) {
  const contentType: string | undefined = attachment.metadata?.contentType;
  const isImage = contentType?.startsWith('image/') ?? false;

  if (isImage) {
    return (
      <ImageAttachment
        attachment={attachment}
        enableImagePreview={enableImagePreview}
      />
    );
  }
  return (
    <DocumentAttachment
      attachment={attachment}
      renderDocumentPreview={renderDocumentPreview}
    />
  );
}

function ImageAttachment({
  attachment,
  enableImagePreview,
}: {
  attachment: MessageAttachment;
  enableImagePreview: boolean;
}) {
  const alt = attachment.metadata?.altText ?? attachment.name ?? 'Image';
  const filename = attachment.name ?? attachment.id;
  const thumbnail = (
    <img
      src={attachment.url}
      alt={alt}
      className={`max-w-[200px] rounded-md${
        enableImagePreview ? 'cursor-pointer' : ''
      }`}
    />
  );
  return (
    <div className="flex items-center gap-1">
      <div className="relative inline-flex max-w-[200px] overflow-hidden rounded-md">
        {enableImagePreview ? (
          <Modal.Root>
            <Modal.Trigger>
              <button
                type="button"
                aria-label={`Open image preview for ${
                  attachment.name ?? 'attachment'
                }`}
              >
                {thumbnail}
              </button>
            </Modal.Trigger>
            <Modal.Content size="large">
              <img
                src={attachment.url}
                alt={alt}
                className="w-full rounded-md"
              />
            </Modal.Content>
          </Modal.Root>
        ) : (
          thumbnail
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-white/70 px-2 py-1">
          <Text type="body-xs" className="truncate text-gray-700">
            {filename}
          </Text>
        </div>
      </div>
      <DownloadIconLink url={attachment.url} name={attachment.name} />
    </div>
  );
}

function DocumentAttachment({
  attachment,
  renderDocumentPreview,
}: {
  attachment: MessageAttachment;
  renderDocumentPreview?: ChatAttachmentsProps['renderDocumentPreview'];
}) {
  const filename = attachment.name ?? 'Attachment';
  const attachmentChip = (
    <Button
      variant="secondary"
      size="xs"
      aria-label={`Preview ${filename}`}
      icon={<FileTypeIcon fileName={filename} />}
    >
      {filename}
    </Button>
  );

  return (
    <div className="flex items-center gap-1">
      {renderDocumentPreview ? (
        <Modal.Root>
          <Modal.Trigger hasReactAriaChildren>{attachmentChip}</Modal.Trigger>
          <Modal.Content size="large" aria-describedby={undefined}>
            <Modal.Header title={filename} hasCloseButton />
            <div
              className={
                getDocumentExtension(attachment.url, attachment.name) === 'csv'
                  ? 'flex max-h-[80vh] min-h-0 flex-col'
                  : 'h-[80vh] min-h-0'
              }
            >
              {renderDocumentPreview(attachment)}
            </div>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1.5">
          <FileTypeIcon fileName={filename} />
          <Text type="body-xs" className="text-gray-700">
            {filename}
          </Text>
        </div>
      )}
      <DownloadIconLink url={attachment.url} name={attachment.name} />
    </div>
  );
}

function FileTypeIcon({ fileName }: { fileName: string }) {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  const Icon = FILE_TYPE_ICONS[extension] ?? PiFile;
  return <Icon size={14} className="shrink-0" />;
}

function DownloadIconLink({
  url,
  name,
}: {
  url: string;
  name: string | undefined;
}) {
  return (
    <Button
      isGhost
      onPress={() => downloadFile(url, name).catch(console.error)}
      aria-label={`Download ${name ?? 'attachment'}`}
      icon={
        <PiDownloadSimple
          size={14}
          className="text-gray-400 hover:text-gray-600"
        />
      }
    />
  );
}
